import React, { useState, useEffect } from "react";
import { auth, googleProvider } from "../firebaseConfig";
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
} from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { FiUser, FiLogOut, FiArrowRight } from "react-icons/fi";
import "./MyAccount.css";

const getFriendlyError = (code) => {
  switch (code) {
    case "auth/email-already-in-use":
      return "An account with this email already exists.";
    case "auth/invalid-credential":
    case "auth/user-not-found":
    case "auth/wrong-password":
      return "Invalid email or password.";
    case "auth/popup-closed-by-user":
      return "Sign-in cancelled. Please try again.";
    case "auth/popup-blocked":
      return "Popup blocked. Please allow popups for this site.";
    case "auth/too-many-requests":
      return "Too many attempts. Please try again later.";
    case "auth/weak-password":
      return "Password must be at least 6 characters.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection.";
    case "auth/operation-not-allowed":
      return "This sign-in method is not enabled.";
    default:
      return "Something went wrong. Please try again.";
  }
};

const MyAccount = () => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSignup, setIsSignup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = location.state?.from;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
      if (currentUser && fromPath) {
        navigate(fromPath, { replace: true });
      }
    });
    return () => unsubscribe();
  }, [fromPath, navigate]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!email || !password) {
      setError("Please fill in all fields.");
      return;
    }

    if (isSignup) {
      if (!name) {
        setError("Please enter your name.");
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
      if (password.length < 6) {
        setError("Password must be at least 6 characters.");
        return;
      }
    }

    setLoading(true);
    try {
      if (isSignup) {
        const credential = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(credential.user, { displayName: name });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err) {
      setError(getFriendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    setLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user") {
        setError(getFriendlyError(err.code));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError("");
    setSuccess("");
    if (!email) {
      setError("Please enter your email to reset password.");
      return;
    }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess("Password reset email sent! Check your inbox.");
    } catch (err) {
      setError(getFriendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  const goToBlogs = () => {
    navigate("/blog");
  };

  if (authLoading) {
    return (
      <div className="my-account-page">
        <div className="account-loading-spinner">
          <div className="auth-spinner" />
          <p>Verifying authentication status...</p>
        </div>
      </div>
    );
  }

  const displayName = user?.displayName || (user?.email ? user.email.split("@")[0] : "User");

  return (
    <div className="my-account-page">
      <div className="account-wrapper">
        {user ? (
          <div className="account-card logged-in-card">
            <div className="logged-in-icon">
              <FiUser />
            </div>

            <h2>Welcome back,</h2>
            <p className="user-identity">{displayName}</p>
            <p className="account-subtitle">You are signed in to Data Bolta Hai</p>

            <div className="account-details-grid">
              <div className="details-row">
                <span className="details-label">User Name</span>
                <span className="details-val font-semibold">{displayName}</span>
              </div>
              <div className="details-row">
                <span className="details-label">Email Address</span>
                <span className="details-val">{user.email}</span>
              </div>
              <div className="details-row">
                <span className="details-label">Account Status</span>
                <span className="details-val">Active</span>
              </div>
            </div>

            <div className="logged-in-actions">
              <button className="vendor-form-btn" onClick={goToBlogs}>
                <span>View Blogs</span>
                <FiArrowRight style={{ marginLeft: "6px" }} />
              </button>
              <button onClick={handleLogout} className="logout-btn">
                <FiLogOut style={{ marginRight: "6px" }} /> Logout
              </button>
            </div>
          </div>
        ) : (
          <div className="account-card auth-card">
            <div className="auth-header">
              <h2>Welcome!</h2>
              <p className="auth-subtitle">
                {isSignup ? "Create your account" : "Sign in to your account"}
              </p>
            </div>

            {error && <div className="auth-error-msg">{error}</div>}
            {success && <div className="auth-success-msg">{success}</div>}

            <form onSubmit={handleAuth} className="auth-form">
              {isSignup && (
                <div className="form-group">
                  <label className="input-label" htmlFor="auth-name">
                    Full Name
                  </label>
                  <input
                    id="auth-name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label className="input-label" htmlFor="auth-email">
                  Email
                </label>
                <input
                  id="auth-email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="input-label" htmlFor="auth-password">
                  Password
                </label>
                <div className="password-input-wrapper">
                  <input
                    id="auth-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="eye-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              {isSignup && (
                <div className="form-group">
                  <label className="input-label" htmlFor="auth-confirm-password">
                    Confirm Password
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      id="auth-confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="eye-toggle-btn"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="primary-auth-btn"
                disabled={loading}
              >
                {loading
                  ? isSignup
                    ? "Creating account..."
                    : "Signing in..."
                  : isSignup
                  ? "Sign Up"
                  : "Login"}
              </button>
            </form>

            <div className="divider">
              <span>OR</span>
            </div>

            <button
              onClick={handleGoogleLogin}
              className="google-auth-btn"
              disabled={loading}
            >
              <svg className="google-icon" width="18" height="18" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
                <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
              </svg>
              {loading ? "Signing in with Google..." : "Continue with Google"}
            </button>

            <div className="auth-links">
              {!isSignup && (
                <button
                  onClick={handleForgotPassword}
                  className="forgot-password-link"
                  disabled={loading}
                >
                  Forgot Password?
                </button>
              )}
              <p
                onClick={() => {
                  setIsSignup(!isSignup);
                  setError("");
                  setSuccess("");
                }}
                className="toggle-auth-link"
              >
                {isSignup
                  ? "Already have an account? Login"
                  : "New user? Sign Up"}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyAccount;
